<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-xl">
        <b-card>
          <template slot="header">
            <survey-filter v-show="exportTypeValue !== ExportType.AggregatedReportFile" v-model="surveyFilter" :disabled="isExporting" :multipleSurveySelection="false" @input="loadData" />
            <client-filter v-show="exportTypeValue === ExportType.AggregatedReportFile" v-model="clientFilter" :disabled="isExporting" :multipleClientSelection="false" @input="loadAggregatedReportFileData" />
          </template>

          <div class="form-row">
            <div class="form-group col-md">
              <b-form-group :label-cols-sm="2" :label="$t('Webportal.ExportType')" label-class="font-weight-bold">
                <b-form-radio-group buttons button-variant="outline-secondary"
                                    v-model="exportTypeValue"
                                    :disabled="isExporting"
                                    @change="exportTypeChanged">
                  <b-form-radio v-if="showExportType(ExportType.Ranking)"
                                :value="ExportType.Ranking">{{ $t('Webportal.Ranking') }}</b-form-radio>
                  <b-form-radio v-if="showExportType(ExportType.RankingComparison)"
                                :value="ExportType.RankingComparison">{{ $t('Webportal.RankingComparison') }}</b-form-radio>
                  <b-form-radio v-if="showExportType(ExportType.RawData)"
                                :value="ExportType.RawData">{{ $t('Webportal.RawData') }}</b-form-radio>
                  <b-form-radio v-if="showExportType(ExportType.PdfReport)"
                                :value="ExportType.PdfReport">{{ $t('Webportal.PdfReport') }}</b-form-radio>
                  <b-form-radio v-if="showExportType(ExportType.AggregatedReportFile) && localUserProfile.Permissions.includes('ClientReports')"
                                :value="ExportType.AggregatedReportFile">{{ $t('Webportal.AggregatedReportFiles') }}</b-form-radio>
                </b-form-radio-group>
              </b-form-group>
            </div>

            <div class="form-group col-md-auto" v-show="exportTypeValue === ExportType.AggregatedReportFile">
              <div class="input-group d-flex justify-content-end">
                <b-pagination v-model="selectedPage"
                              :total-rows="totalCount"
                              :per-page="pageSize"
                              :limit="5"
                              :disabled="isExporting"
                              @change="getAggregatedReportFiles"
                              class="mb-0"></b-pagination>
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ShowAllChecks')">
                    <b-button variant="primary"
                              :disabled="getAllAggregatedReportFilesDisabled"
                              @click="getAllAggregatedReportFiles">
                      {{ $n(this.totalCount) }}
                    </b-button>
                  </span>
              </div>
            </div>
            <div class="form-group col-md-auto"
                v-show="exportTypeValue === ExportType.AggregatedReportFile"
                v-b-tooltip.hover.nofade="toolTipDelay"
                :title="$t('Webportal.DownloadAggregatedReportFiles')">
              <b-button variant="primary"
                        :disabled="isExporting || downloadAggregatedReportFilesDisabled"
                        @click="downloadAggregatedReportFilesPackage()">
                <font-awesome-icon icon="file-download" />
              </b-button>
            </div>
          </div>

          <div v-show="exportTypeValue !== ExportType.AggregatedReportFile">
            <b-form-group v-if="isMSMUser"
                          :label-cols-sm="2" :label="$t('Webportal.Status')" label-class="font-weight-bold"
                          :disabled="isExporting">
              <b-form-group>
                <div class="form-row">
                  <div class="col-auto">
                    <b-form-input v-model="statusMinValue"
                                  type="number"
                                  min="0"
                                  max="400"
                                  style="max-width:5em"></b-form-input>
                  </div>
                  <div class="col-auto">
                    <label class="col-form-label">{{ $t('Webportal.RangeTo') }}</label>
                  </div>
                  <div class="col-auto">
                    <b-form-input v-model="statusMaxValue"
                                  type="number"
                                  min="0"
                                  max="400"
                                  style="max-width:5em"></b-form-input>
                  </div>
                </div>
              </b-form-group>
              <b-form-group>
                <b-form-checkbox v-model="includeTestchecks"
                                :disabled="noSurveySelected || isExporting">
                                {{ $t('Webportal.Include') }} {{ $t('Webportal.IncludeTestchecks') }}</b-form-checkbox>
              </b-form-group>
            </b-form-group>

            <b-form-group v-show="exportTypeValue === ExportType.Ranking || exportTypeValue === ExportType.RankingComparison"
                          :label-cols-sm="2" :label="$t('Webportal.TopResultCount')" label-class="font-weight-bold">
              <b-form-select class="col-xl-2 col-lg-2 col-md-3 col-sm-4" v-model="topResultCount">
                <option :value="null">{{ $t('Webportal.Unlimited') }}</option>
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="100">100</option>
              </b-form-select>
            </b-form-group>

            <b-form-group v-show="exportTypeValue !== ExportType.PdfReport" :label-cols-sm="2" :label="$t('Webportal.AggregationLevel')" label-class="font-weight-bold">
              <treeselect class="d-inline-flex"
                          v-model="aggregationLevel1"
                          :clearable="false"
                          :deleteRemoves="false"
                          :backspaceRemoves="false"
                          :auto-load-root-options="false"
                          :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                          :options="aggregationItems"
                          :disabled="noSurveySelected || isExporting"
                          :disableFuzzyMatching="true"
                          :multiple="false"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1"
                          :disable-branch-nodes="true"
                          style="max-width:20em">
              </treeselect>
              <treeselect class="d-inline-flex"
                          v-if="exportTypeValue !== ExportType.PdfReport"
                          v-model="aggregationLevel2"
                          :clearable="exportTypeValue !== ExportType.RankingComparison"
                          :auto-load-root-options="false"
                          :placeholder="$t('Webportal.AggregationLevel2')"
                          :options="aggregationItems"
                          :disabled="noSurveySelected || isExporting"
                          :disableFuzzyMatching="true"
                          :multiple="false"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1"
                          :disable-branch-nodes="true"
                          style="max-width:20em">
              </treeselect>
            </b-form-group>

            <div v-show="exportTypeValue === ExportType.Ranking || exportTypeValue === ExportType.RawData ||
                         exportTypeValue === ExportType.RankingComparison || exportTypeValue === ExportType.PdfReport">
              <b-form-group :label-cols-sm="2" label-class="font-weight-bold">
                <template slot="label">
                  <b-badge style="background-color: #007bff;">&nbsp;&nbsp;</b-badge>
                  {{ $t('Webportal.Kpis') }}
                </template>
                <b-form-group>
                  <treeselect ref="kpiTreeselect"
                              v-model="kpiValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                              :options="kpiItems"
                              valueFormat="object"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :defaultExpandLevel="1">
                  </treeselect>
                </b-form-group>
                <b-form-checkbox v-if="hasRawDataPointSelection()" v-show="exportTypeValue !== ExportType.PdfReport"
                                 v-model="includeKpiPoints"
                                 :disabled="noSurveySelected || isExporting">
                                 {{ $t('Webportal.Include') }} {{ $t('Webportal.KpiPoints') }}</b-form-checkbox>
              </b-form-group>

              <b-form-group v-show="exportTypeValue !== ExportType.PdfReport && exportTypeValue !== ExportType.RankingComparison"
                            :label-cols-sm="2" label-class="font-weight-bold pt-0">
                <template slot="label">
                  <b-badge style="background-color: #6c757d;">&nbsp;&nbsp;</b-badge>
                  {{ $t('Webportal.ClusterResults') }}
                </template>
                <b-form-checkbox inline v-model="includeClusterPercentages" :disabled="noSurveySelected || isExporting">
                                {{ $t('Webportal.Include') }} {{ $t('Webportal.ClusterResults') }}</b-form-checkbox>
                <b-form-checkbox inline v-if="hasRawDataPointSelection()"
                                 v-model="includeClusterPoints" :disabled="noSurveySelected || isExporting || !includeClusterPercentages">
                                {{ $t('Webportal.Include') }} {{ $t('Webportal.ClusterPoints') }}</b-form-checkbox>
              </b-form-group>

              <b-form-group v-show="exportTypeValue === ExportType.PdfReport"
                            :label-cols-sm="2" label-class="font-weight-bold pt-0">
                <template slot="label">
                  <font-awesome-icon :icon="['fas', 'dice']" />
                  {{ $t('Webportal.AnswerFrequencies') }}
                </template>
                <b-form-checkbox inline v-model="includeFrequencies" :disabled="noSurveySelected || isExporting">
                  {{ $t('Webportal.Include') }} {{ $t('Webportal.AnswerFrequencies') }}
                </b-form-checkbox>
              </b-form-group>
            </div>

            <b-form-group v-show="exportTypeValue !== ExportType.PdfReport" :label-cols-sm="2" :label="$t('Webportal.Options')" label-class="font-weight-bold pt-0">
              <div v-if="IsColorizeResultsVisible">
                <b-form-checkbox inline v-model="colorizeResults" :disabled="noSurveySelected || isExporting">
                                {{ $t('Webportal.ColorizeResults') }}</b-form-checkbox>
                <b-form-radio-group buttons button-variant="outline-primary" size="sm"
                                    v-model="colorizeBackground"
                                    :disabled="!colorizeResults || noSurveySelected || isExporting">
                  <b-form-radio :value="true">{{ $t('Webportal.BackgroundColor') }}</b-form-radio>
                  <b-form-radio :value="false">{{ $t('Webportal.FontColor') }}</b-form-radio>
                </b-form-radio-group>
              </div>
              <b-form-checkbox v-model="useCompactLayout" :disabled="noSurveySelected || isExporting">
                              {{ $t('Webportal.CompactLayout') }}</b-form-checkbox>
            </b-form-group>

            <div class="form-row">
              <div class="col-md-auto">
                <b-alert :show="noDataAlert"
                        dismissible
                        @dismissed="noDataAlert=0"
                        variant="warning">
                  {{ $t('Webportal.NoDataFound') }}
                </b-alert>
                <b-alert v-model="errorAlert" dismissible variant="danger">
                  {{ $t('Webportal.AggregatedReportError') }}
                </b-alert>
                <b-button variant="primary"
                          :disabled="noSurveySelected || isExporting || isPreviewLoading"
                          @click="downloadRawData()">
                  {{ $t('Webportal.Download') }}
                  <font-awesome-icon icon="download" />
                </b-button>
                <b-button v-show="exportTypeValue === ExportType.PdfReport"
                          variant="primary"
                          :disabled="noSurveySelected || isExporting || isPreviewLoading"
                          @click="showPreview('Aggregated Report')">
                  {{ $t('Webportal.ShowPreview') }}
                  <font-awesome-icon icon="eye" />
                </b-button>
              </div>
              <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
            </div>
          </div>

          <div v-show="exportTypeValue === ExportType.AggregatedReportFile"
              v-bind:class="{ spinner: isLoading || isExporting }"
              style="min-height:300px;">
            <!-- Important: use @click.stop and @click.native.stop for action items inside table
                https://bootstrap-vue.js.org/docs/components/table/ -->
            <b-table v-show="!this.noClientSelected"
                    small hover responsive :fixed="browserIsIE"
                    :foot-clone="reportFileItems && reportFileItems.length >= 5"
                    :items="reportFileItems"
                    :fields="fields"
                    no-local-sorting
                    @row-clicked="rowClicked"
                    @sort-changed="sortChanged">
              <template v-slot:head(Select)>
                <b-form-checkbox v-model="toggleSelectionChecked"
                                :indeterminate="toggleSelectionIndeterminate"
                                style="min-height:1.0rem"
                                @click.native.stop
                                @change="toggleSelection"
                                v-b-tooltip.hover.nofade="toolTipDelay"
                                :title="$t('Webportal.SelectAll')" />
              </template>

              <template slot="top-row">
                <td></td>
                <td>
                  <search-select small v-model="columnFilter.AggregatedReportFile.AggregationLevel"
                                :options="aggregationLevelOptions" text-field="PropertyName" value-field="PropertyName"
                                @change="loadAggregatedReportFileData"
                                :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                </td>
                <td>
                  <search-select small v-model="columnFilter.AggregatedReportType.Name"
                                :options="aggregatedReportTypeNameOptions" text-field="Name" value-field="Name"
                                @change="loadAggregatedReportFileData"
                                :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.AggregatedReportFile.DateCreated" @change="loadAggregatedReportFileData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td></td>
                <td>
                  <search-input small v-model.trim="columnFilter.AggregatedReportFile.DisplayText" @change="loadAggregatedReportFileData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.ClientLocationIdOnClientSite" @change="loadAggregatedReportFileData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.FullAddress" @change="loadAggregatedReportFileData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td></td>
                <td></td>
              </template>

              <template v-slot:cell(Select)="data">
                <b-form-checkbox v-model="data.item.Selected"
                                @click.native.stop
                                @change="updateToggleSelection" />
              </template>
              <template v-slot:[`cell(AggregatedReportFile.AggregationLevel)`]="data">
                {{ data.value }}
              </template>
              <template v-slot:[`cell(AggregatedReportType.Name)`]="data">
                {{ data.value }}
              </template>
              <template v-slot:[`cell(AggregatedReportFile.FileName)`]="data">
                <font-awesome-icon :icon="getFileIcon(data.value)" size="2x" />
              </template>
              <template v-slot:[`cell(AggregatedReportFile.DateCreated)`]="data">
                {{ data.value | msm_yearmonth }}
              </template>
              <template v-slot:[`cell(AggregatedReportFile.DisplayText)`]="data">
                {{ data.value }}
              </template>
              <template v-slot:cell(ClientLocation)="data">
                <span v-if="data.value">
                  {{ data.value.Name }}<br>
                  {{ data.value.Address }}<br>
                  {{ data.value.City }}, {{ data.value.PostalCode }}
                </span>
              </template>
              <template v-slot:cell(ActionDownload)="data">
                <b-button variant="primary" size="sm"
                          v-b-tooltip.hover.nofade="toolTipDelay"
                          title="Download"
                          @click.stop="downloadAggregatedReportFile(data.item.AggregatedReportFile.AggregatedReportFileId)">
                  <font-awesome-icon icon="file-download" fixed-width />
                </b-button>
              </template>
              <template v-slot:cell(ActionPreview)="data">
                <b-button v-if="data.item.AggregatedReportFile.FileName.endsWith('.pdf')"
                          variant="primary" size="sm"
                          v-b-tooltip.hover.nofade="toolTipDelay"
                          :title="$t('Webportal.ShowPreview')"
                          @click.stop="showPreview(data.item.AggregatedReportFile.FileName, data.item.AggregatedReportFile.AggregatedReportFileId)">
                  <font-awesome-icon icon="eye" fixed-width />
                </b-button>
              </template>
            </b-table>
          </div>
        </b-card>
      </div>

      <div class="col-xl-5 p-0 pdf-viewer-container-xxl" v-bind:class="{ 'd-none': !isPreviewVisible }">
        <div class="pdf-viewer card col-5 p-0">
          <div>
            <button type="button" class="close" aria-label="Close" @click="closePreview()"
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.ClosePreview')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="card-body pdf-viewer-height pt-0" v-bind:class="{ spinner: isPreviewLoading }">
            <iframe class="pdf-viewer-height w-100" :src="previewUrl" frameBorder="0"></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';

import SurveyFilter from '@/components/SurveyFilter-APP_TARGET';
import ClientFilter from '@/components/ClientFilter-APP_TARGET';
import SearchInput from '@/components/SearchInput'
import SearchSelect from '@/components/SearchSelect'

import AuthenticationService from '@/services/AuthenticationService'
import SurveysService from '@/services/SurveysService';
import AggregatedReportsService from '@/services/AggregatedReportsService';

const ExportType = {
  Ranking: 1,
  RankingComparison: 2,
  RawData: 3,
  AggregatedReportFile: 4,
  PdfReport: 5
}

const PAGE_SIZE_DEFAULT = 20;
const PAGE_SIZE_MAXIMUM = 999999;

export default {
  name: "AggregatedReports",

  components: { SurveyFilter, ClientFilter, SearchInput, SearchSelect },

  mixins: [ AppConfig, DownloadFileMixin ],

  data () {
    return {
      // Filter properties
      surveyFilter: [],
      currentSurveyFilter: null,
      clientFilter: [],
      aggregationItems: [],                 // Data items for aggregation treeselect
      aggregationLevel1: null,              // Selected aggregation item
      aggregationLevel2: null,              // Selected aggregation item level 2
      statusMinValue: 220,                  // Customer Min/Max Values
      statusMaxValue: 220,

      ExportType: ExportType,
      exportTypeValue: this.defaultExportType(),
      topResultCount: null,
      includeKpiPoints: false,
      kpiItems: [],                        // Data items for kpi treeselect
      kpiValue: [],                        // Selected kpi items
      includeClusterPercentages: false,
      includeClusterPoints: false,
      includeFrequencies: false,
      includeTestchecks: false,
      colorizeResults: false,
      colorizeBackground: true,
      useCompactLayout: false,

      isExporting: false,
      noDataAlert: 0,
      errorAlert: false,

      isLoading: false,                     // Determines if data loading is currently in progress

      columnFilter: {                       // Filter values for each column
        AggregatedReportFile:{
          AggregationLevel: null,
          DateCreated: null,
          FileName: null,
          DisplayText: null},
        AggregatedReportType: { Name: null },
        ClientLocation: {
          ClientLocationIdOnClientSite: null,
          FullAddress: null }
      },

      sortBy: null,                         // Sorting column
      sortDescending: false,                // Sorting order

      currentFilter: null,                  // Current filter object for change detection

      // Paging properties
      pageSize: PAGE_SIZE_DEFAULT,          // Number of items on one page of the table
      selectedPage: 1,                      // Number of the page, that is currently visible
      totalCount: 0,                        // Number of total items

      // Table properties
      aggregationLevelOptions: [],
      aggregatedReportTypeNameOptions: [],
      reportFileCancelToken: null,
      reportFileItems: [],
      fields: [
        { key: 'Select', thStyle: 'width:2em' },
        { key: 'AggregatedReportFile.AggregationLevel', label: this.$t('Webportal.AggregationLevel'), thStyle: 'width:12em', sortable: true },
        { key: 'AggregatedReportType.Name', label: this.$t('Webportal.Type'), thStyle: 'width:10em', sortable: true },
        { key: 'AggregatedReportFile.DateCreated', label: this.$t('Webportal.Created'), class: 'text-center', thStyle: 'width:7em', sortable: true },
        { key: 'AggregatedReportFile.FileName', class: 'text-center', label: '', thStyle: 'width:3em' },
        { key: 'AggregatedReportFile.DisplayText', label: this.$t('Webportal.Filename'), thStyle: '', sortable: true },
        { key: 'ClientLocation.ClientLocationIdOnClientSite', label: this.$t('Webportal.LocationId'), thStyle: 'width:8em', sortable: true },
        { key: 'ClientLocation', label: this.$t('Webportal.LocationAddress'), thStyle: '', sortable: true },
        { key: 'ActionDownload', label: '', thStyle: 'width:3em' },
        { key: 'ActionPreview', label: '', thStyle: 'width:3em' }
      ],

      toggleSelectionChecked: false,
      toggleSelectionIndeterminate: false,

      previewUrl: null,
      isPreviewLoading: false,
      isPreviewVisible: false,
      previewCancelToken: null,
    }
  },

  computed: {
    isMSMUser: function () {
      return AuthenticationService.isMSMUser;
    },

    localUserProfile: function() {
      return AuthenticationService.localUserProfile;
    },

    noSurveySelected: function () {
      return _.isEmpty(this.surveyFilter.computedSurveyIds);
    },

    noClientSelected: function () {
      return _.isEmpty(this.clientFilter.clientIds);
    },

    filter: function() {
      // Returns an array of all KPI ids recursively for the given node
      let returnKpiIds = (obj) => {
          return _.concat(obj.id && obj.id[0] === 'K' ? obj.id.substring(1) : [],
              obj.children ? _.flatMapDeep(obj.children, returnKpiIds) : []);
      };
      // Creates a new object without empty values from source
      const isNotEmptyPredicate = o => !_.isEmpty(o);
      const pickByDeep = (obj, fn) =>
        _.pickBy( // Remove on root level
          _.mapValues(obj, (val, key) =>
            !_.isObjectLike(val) ? val : // Take plain values
              !_.isEmpty(_.pickBy(val, fn)) ? _.pickBy(val, fn) : null) // Map values of nested object
          , fn)

      if (this.exportTypeValue !== ExportType.AggregatedReportFile)
        return {
          surveyIds: this.surveyFilter.computedSurveyIds,
          waveIds: this.surveyFilter.waveIds,
          scenarioIds: this.surveyFilter.scenarioIds,
          dateFrom: this.surveyFilter.dateFrom,
          dateTo: this.surveyFilter.dateTo,
          propertyIds: this.surveyFilter.propertyIds,
          locationIds: this.surveyFilter.clientLocationIds,
          kpiIds: !_.isEqual(this.kpiValue, this.kpiItems) ? _.flatMapDeep(this.kpiValue, returnKpiIds) : null,
          surveyInstanceStatusMinimum: this.isMSMUser ? this.statusMinValue : null,
          surveyInstanceStatusMaximum: this.isMSMUser ? this.statusMaxValue : null,
          includeTestchecks: this.includeTestchecks
        };
      else
        return {
          clientIds: this.clientFilter.clientIds,
          propertyIds: this.clientFilter.propertyIds,
          columnFilter: pickByDeep(this.columnFilter, isNotEmptyPredicate),
          sortBy: this.sortBy,
          sortDescending: this.sortDescending,
        };
    },

    options: function() {
      if (this.exportTypeValue !== ExportType.AggregatedReportFile)
      {
        let result = {
          aggregationLevel1: this.aggregationLevel1,
          aggregationLevel2: this.aggregationLevel2,
          pivotAggregationLevel2: this.exportTypeValue === ExportType.RankingComparison,
          isRanking: this.exportTypeValue === ExportType.Ranking || this.exportTypeValue === ExportType.RankingComparison,
          colorizeResults: this.colorizeResults,
          colorizeBackground: this.colorizeBackground,
          useCompactLayout: this.useCompactLayout
        };

        if (result.isRanking)
          result.topResultCount = this.topResultCount;

        if (this.exportTypeValue === ExportType.Ranking || this.exportTypeValue === ExportType.RawData)
        {
          result.includeKpiPercentages = _.size(this.kpiValue) > 0;
          result.includeKpiPoints = this.hasRawDataPointSelection() && this.includeKpiPoints && _.size(this.kpiValue) > 0;
          result.includeClusterPercentages = this.includeClusterPercentages;
          result.includeClusterPoints = this.hasRawDataPointSelection() && this.includeClusterPercentages && this.includeClusterPoints;
          result.includeOverallPoints = this.hasRawDataPointSelection();
        }
        else if (this.exportTypeValue === ExportType.RankingComparison)
        {
          result.includeKpiPercentages = _.size(this.kpiValue) > 0;
          result.includeKpiPoints = this.hasRawDataPointSelection() && this.includeKpiPoints && _.size(this.kpiValue) > 0;
          result.includeOverallPoints = this.hasRawDataPointSelection();
        }

        if (this.exportTypeValue === ExportType.PdfReport)
        {
          result.includeKpis = _.size(this.kpiValue) > 0;
          result.includeFrequencies = this.includeFrequencies;
        }

        return result;
      }
      else
        return { page: this.selectedPage, pageSize: this.pageSize }
    },

    getAllAggregatedReportFilesDisabled: function () {
      return this.totalCount <= PAGE_SIZE_DEFAULT || this.totalCount >= 1000 || this.pageSize === PAGE_SIZE_MAXIMUM;
    },

    selectedAggregatedReportFileIds: function () {
      return _.map(this.reportFileItems.filter(item => item.Selected), 'AggregatedReportFile.AggregatedReportFileId');
    },

    downloadAggregatedReportFilesDisabled: function () {
      return _.isEmpty(this.reportFileItems) || _.isEmpty(this.selectedAggregatedReportFileIds);
    },

    defaultAggregationItems: function() {
      return [ { id: 'Location', label: this.$t('Webportal.LocationId') },
               { id: 'Wave', label: this.$t('Webportal.Wave') },
               { id: 'Scenario', label: this.$t('Webportal.Scenario') } ];
    },

    IsColorizeResultsVisible: function() {
      return APP_TARGET !== 'bmw' && APP_TARGET !== 'db';
    }
  },

  mounted() {
    this.initializeTheme();
    this.aggregationItems = this.defaultAggregationItems
  },

  methods: {
    showExportType(exportTypeValue) {
      if (APP_TARGET === "porschesar")
        return ExportType.AggregatedReportFile == exportTypeValue;
      else
        return true;
    },
    
    defaultExportType() {
      if (APP_TARGET === "porschesar")
        return ExportType.AggregatedReportFile;
      else
        return ExportType.Ranking;
    },

    // Get survey structure data for the structure treeselect
    getSurveyProperties() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyCustomProperties(this.filter)
          .then(response => {
            let properties = response.data;
            if (properties && properties.length > 0)
              properties[0].children.forEach(p => p.id = `Property^${p.id}` );
            this.aggregationItems = this.defaultAggregationItems.concat(properties);
          })
      };
    },

    // Get survey structure data for the structure treeselect
    getSurveyKpis() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyKpis({ surveyIds: this.surveyFilter.computedSurveyIds })
          .then(response => {
            // if only one survey selected/available, hide it
            if (response.data && response.data.length === 1 && response.data[0].children) {
              this.kpiItems = response.data[0].children;
            } else {
              this.kpiItems = [];
            }

            // Preselect all root items
            setTimeout(() => {
              this.kpiValue = this.kpiItems;
              this.includeClusterPercentages = _.size(this.kpiItems) === 0;
              this.includeClusterPoints = false;
            }, 0)
          })
      };
    },

    // Gets survey instances data for the table from the server, including current filter and pagination
    getAggregatedReportFiles(page) {
      if (this.exportTypeValue !== ExportType.AggregatedReportFile)
         return;

      this.isLoading = true;

      if (page) this.selectedPage = page;

      // Cancel previous request
      if (this.reportFileCancelToken) {
        this.reportFileCancelToken.cancel();
      }

      this.reportFileCancelToken = axios.CancelToken.source();

      AggregatedReportsService.getAggregatedReportFiles(this.filter, this.options, this.reportFileCancelToken)
        .then(response => {
          this.totalCount = response.data.TotalCount;
          this.reportFileItems = response.data.items;
          this.aggregatedReportTypeNameOptions = response.data.AggregatedReportTypes
          this.aggregationLevelOptions = response.data.AggregationLevels
          this.isLoading = false;
          this.toggleSelectionChecked = false;
          this.toggleSelectionIndeterminate = false;
      })
      .catch((thrown) => {
        // TODO
      });
    },

    getAllAggregatedReportFiles() {
      this.selectedPage = 1;
      this.pageSize = PAGE_SIZE_MAXIMUM;
      this.getAggregatedReportFiles();
    },

    loadData() {
      if (!_.isEqual(this.surveyFilter?.computedSurveyIds, this.currentSurveyFilter?.computedSurveyIds)) {
        this.currentSurveyFilter = this.surveyFilter;

        this.aggregationLevel1 = 'Location';
        this.aggregationLevel2 = this.exportTypeValue === ExportType.RankingComparison ? 'Wave' : null;
        this.kpiValue = [];

        this.getSurveyProperties();
        this.getSurveyKpis();
      }
    },

    loadAggregatedReportFileData() {
      if (!_.isEqual(this.filter, this.currentFilter)) {
        this.currentFilter = this.filter;

        this.selectedPage = 1;
        this.pageSize = PAGE_SIZE_DEFAULT;

        this.getAggregatedReportFiles();
      }
    },

    exportTypeChanged(exportTypeValue) {
      if (exportTypeValue !== ExportType.AggregatedReportFile)
      {
        this.includeKpiPoints = exportTypeValue === ExportType.RawData;
        this.aggregationLevel2 = this.aggregationLevel2 || (!this.noSurveySelected && exportTypeValue === ExportType.RankingComparison ? 'Wave' : null);
      }
    },

    downloadRawData() {
      this.isExporting = true;
      this.noDataAlert = 0;
      this.errorAlert = false;

      if (this.exportTypeValue === ExportType.PdfReport)
        var request = AggregatedReportsService.getAggregatedPdfReport(this.filter, this.options);
      else
        var request = AggregatedReportsService.getRawDataAggregated(this.filter, this.options);

      request
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.noDataAlert = 5;
          else
            this.downloadFile(response);
        })
        .catch(_ => 
        { 
          this.isExporting = false; 
          this.errorAlert = true;
        });
    },

    getFileIcon(fileName)
    {
      return ['far',
        _.endsWith(fileName, '.pdf') ? 'file-pdf' :
        _.endsWith(fileName, '.pptx') ? 'file-powerpoint' : 'file'];
    },

    // Selects a row when clicked
    rowClicked(item) {
      let checked = item.Selected ? !item.Selected : true;

      this.updateToggleSelection(checked);

      // Use $set to register the new property for vue tracking
      this.$set(item, 'Selected', checked);
    },

    toggleSelection(checked) {
      _.forEach(this.reportFileItems, item => {
          this.$set(item, 'Selected', checked);
        });
    },

    updateToggleSelection(checked) {
      let count = 0;

      _.forEach(this.reportFileItems, item => {
          if (item.Selected) count++;
        });

      // New value is not written to property right now
      checked ? count++ : count--;

      // Handle changes in individual flavour checkboxes
      if (count === 0) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = false;
      } else if (count === this.reportFileItems.length) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = true;
      } else {
        this.toggleSelectionIndeterminate = true;
        this.toggleSelectionChecked = false;
      }
    },

    sortChanged(e) {
      this.sortBy = e.sortBy;
      this.sortDescending = e.sortDesc;
      this.loadAggregatedReportFileData();
    },

    showPreview(title, id) {
      // Check for pdf-viewer-breakpoint
      if (window.innerWidth < 1600)
      {
        // Open in new tab
        var win = window.open('', '_blank');
        win.document.title = title;
        win.focus();

        if (this.exportTypeValue === ExportType.PdfReport)
          var request = AggregatedReportsService.getAggregatedPdfReport(this.filter, this.options);
        else
          var request = AggregatedReportsService.getAggregatedReportFile(id)

        request
          .then((response) => {
            const blobUrl = window.URL.createObjectURL(response.data);
            win.location.href = `${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;
          });
      }
      else
      {
        // Open in preview window
        if (this.previewCancelToken)
          this.previewCancelToken.cancel('showPreview');

        this.previewCancelToken = axios.CancelToken.source();

        this.isPreviewLoading = true;
        this.isPreviewVisible = true;
        this.noDataAlert = 0;
        this.errorAlert = false;

        if (this.exportTypeValue === ExportType.PdfReport)
          var request = AggregatedReportsService.getAggregatedPdfReport(this.filter, this.options, this.previewCancelToken);
        else
          var request = AggregatedReportsService.getAggregatedReportFile(id, this.previewCancelToken)

        request
          .then((response) => {
            this.isPreviewLoading = false;

            if (response.status === 204) {
              this.noDataAlert = 5;
              this.previewUrl = null;
            }
            else {
              const blobUrl = window.URL.createObjectURL(response.data);
              this.previewUrl =`${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;
            }
          })
          .catch((thrown) => {
            if (thrown.message != 'showPreview')
            {
              this.previewUrl = null;
              this.isPreviewLoading = false;
            }

            if (!axios.isCancel(thrown)) {
              this.errorAlert = true;
            }
          });
      }
    },

    closePreview() {
      this.isPreviewLoading = false;
      this.isPreviewVisible = false;
      this.previewUrl = null;

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('closePreview');
    },

    downloadAggregatedReportFile(id) {
      this.isExporting = true;

      AggregatedReportsService.getAggregatedReportFile(id)
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
        })
        .catch(_ => { this.isExporting = false; } );
    },

    downloadAggregatedReportFilesPackage() {
      this.isExporting = true;

      AggregatedReportsService.getAggregatedReportFilesPackage(this.selectedAggregatedReportFileIds)
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
        })
        .catch(_ => { this.isExporting = false; } );
    },
  }
};
</script>
